import { getTransporterApiBaseUrl } from "./api-base";

export type OnboardingRecordDto = {
  id: string;
  step: string;
  completedAt: string | null;
  failedAt: string | null;
  failureReason: string | null;
  metadata: unknown;
  createdAt: string;
};

/** Ordered steps for transporter onboarding (DEV-056). */
export const TRANSPORTER_ONBOARDING_FLOW = [
  "kyb_verification",
  "bank_account_linking",
  "fee_schedule_agreement",
  "vehicle_registration",
  "driver_registration",
  "completed",
] as const;

export type TransporterOnboardingStep = (typeof TRANSPORTER_ONBOARDING_FLOW)[number];

export async function fetchTransporterOnboarding(
  getToken: () => Promise<string | null>,
  transporterId: string,
): Promise<{ records: OnboardingRecordDto[] }> {
  const token = await getToken();
  if (!token) throw new Error("not_signed_in");
  const base = getTransporterApiBaseUrl();
  const path = `/api/v1/transporters/${encodeURIComponent(transporterId)}/onboarding`;
  const url = base ? `${base}${path}` : path;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`onboarding_list_http_${res.status}`);
  return (await res.json()) as { records: OnboardingRecordDto[] };
}

export async function completeTransporterOnboarding(
  getToken: () => Promise<string | null>,
  transporterId: string,
  body: {
    step: TransporterOnboardingStep;
    metadata?: Record<string, unknown>;
    plaidItemId?: string;
    plaidAccessToken?: string;
    bankAccountLast4?: string | null;
    bankName?: string | null;
    straddlePaykey?: string;
    straddleAccountId?: string | null;
  },
): Promise<{ record: OnboardingRecordDto }> {
  const token = await getToken();
  if (!token) throw new Error("not_signed_in");
  const base = getTransporterApiBaseUrl();
  const path = `/api/v1/transporters/${encodeURIComponent(transporterId)}/onboarding/complete`;
  const url = base ? `${base}${path}` : path;
  const res = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`onboarding_complete_http_${res.status}:${t.slice(0, 240)}`);
  }
  return (await res.json()) as { record: OnboardingRecordDto };
}

export async function failTransporterOnboarding(
  getToken: () => Promise<string | null>,
  transporterId: string,
  body: { step: TransporterOnboardingStep; failureReason: string; metadata?: Record<string, unknown> },
): Promise<{ record: OnboardingRecordDto }> {
  const token = await getToken();
  if (!token) throw new Error("not_signed_in");
  const base = getTransporterApiBaseUrl();
  const path = `/api/v1/transporters/${encodeURIComponent(transporterId)}/onboarding/fail`;
  const url = base ? `${base}${path}` : path;
  const res = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`onboarding_fail_http_${res.status}:${t.slice(0, 240)}`);
  }
  return (await res.json()) as { record: OnboardingRecordDto };
}

export function deriveTransporterOnboardingStep(
  records: OnboardingRecordDto[],
  profileOnboardingCompletedAt: string | null,
): { done: true } | { done: false; step: TransporterOnboardingStep; failedReason: string | null } {
  if (profileOnboardingCompletedAt) return { done: true };
  for (const step of TRANSPORTER_ONBOARDING_FLOW) {
    const rec = records.find((r) => r.step === step);
    if (!rec?.completedAt) {
      return {
        done: false,
        step,
        failedReason: rec?.failedAt ? rec.failureReason : null,
      };
    }
  }
  return { done: true };
}
